import { GetFormalOffersResponse } from '@iwoca/lapi-client/edge';

import {
  useGetBuyerSpendingLimit,
  useGetFormalOffers,
} from '../../../api/lending/lapiHooks';

type TDuration = 1 | 3 | 12;

const DURATION_TO_ID = {
  1: 'THIRTY_DAYS',
  3: 'THREE_MONTHS',
  12: 'TWELVE_MONTHS',
} as const;

export type TSpendingLimitType =
  (typeof DURATION_TO_ID)[keyof typeof DURATION_TO_ID];

const mapOfferDurationToMonths = (
  offer: NonNullable<
    NonNullable<GetFormalOffersResponse['data']>['formal_offers']
  >[number],
): TDuration => {
  if (offer.duration?.unit === 'days') {
    return Math.floor(offer.duration.amount / 30) as TDuration;
  }

  return offer.duration?.amount as TDuration;
};

const useGetIwocapayFormalOffers = () => {
  const { formalOffers, loadingFormalOffers } = useGetFormalOffers();

  return {
    formalOffers:
      formalOffers?.formal_offers.filter(
        (offer) => offer.product_type === 'iwocapay',
      ) || [],
    loadingFormalOffers,
  };
};

export type SpendingLimitEntry = {
  duration: TSpendingLimitType;
  availableAmount: number;
  maxAmount: number;
  interestRate: number;
};

export const useGetSpendingLimits = () => {
  const { spendingLimit, loadingSpendingLimit } = useGetBuyerSpendingLimit();
  const { formalOffers } = useGetIwocapayFormalOffers();

  const spendingLimitData = spendingLimit?.data;

  if (
    formalOffers.length === 0 ||
    !spendingLimitData ||
    !spendingLimitData.spending_limits
  ) {
    return {
      spendingLimits: { isValid: false, limits: [] as SpendingLimitEntry[] },
      loadingSpendingLimits: loadingSpendingLimit,
    };
  }

  const limits: SpendingLimitEntry[] = spendingLimitData.spending_limits
    .map((limit) => {
      const id =
        DURATION_TO_ID[limit.duration_in_months as keyof typeof DURATION_TO_ID];

      const formalOffer = formalOffers.find((offer) => {
        if (!offer.duration) return false;
        return mapOfferDurationToMonths(offer) === limit.duration_in_months;
      });

      if (!formalOffer) return null;

      return {
        duration: id,
        availableAmount: limit.available_amount ?? 0,
        maxAmount: limit.max_amount ?? 0,
        interestRate: formalOffer.interest_rates![0].rate!,
      };
    })
    .filter((entry): entry is SpendingLimitEntry => entry !== null);

  const isValid = formalOffers.some((offer) => offer.is_valid);

  return {
    spendingLimits: {
      isValid,
      limits,
    },
    loadingSpendingLimits: loadingSpendingLimit,
  };
};
